"use client";

import EyeCloseIcon from "@/components/atoms/svg/icons/EyeCloseIcon";
import EyeIcon from "@/components/atoms/svg/icons/EyeIcon";
import { Input } from "@/components/ui/input";
import { EMAIL_PATTERN } from "@/constants/regex";
import useLogin from "@/features/authentication/hooks/useLogin";
import { IAuthData } from "@/features/authentication/interfaces/IAuth";
import clsx from "clsx";
import { useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

export default function FormLogin() {
  const { onSubmit, showPassword, setShowPassword } = useLogin();
  const method = useForm<Pick<IAuthData, "email" | "password">>({
    defaultValues: {
      // TODO: Need to report this issue when sending "" to API should not be allowed
      email: "",
      password: "",
    },
  });

  // Check button disabled when form is invalid
  const isDisabled = useMemo(() => {
    return !method.formState.isValid;
  }, [method.formState.isValid]);

  return (
    <FormProvider {...method}>
      <form
        onSubmit={method.handleSubmit(onSubmit)}
        className="space-y-4 min-w-[352px]"
      >
        {/* Email */}
        <Controller
          control={method.control}
          name="email"
          rules={{
            required: "Email is required",
            pattern: { value: EMAIL_PATTERN, message: "Invalid email address" },
          }}
          render={({ field }) => (
            <div className="relative">
              <Input
                type="email"
                value={field.value}
                onChange={field.onChange}
                placeholder="Email"
                className="base-input"
              />
              {method.formState.errors.email && (
                <span className="text-sm text-destructive">
                  {method.formState.errors.email.message}
                </span>
              )}
            </div>
          )}
        />

        {/* Password */}
        <Controller
          control={method.control}
          name="password"
          rules={{
            required: "Password is required",
          }}
          render={({ field }) => (
            <div className="space-y-2">
              <div className="relative flex items-center justify-between">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Password"
                  className="base-input"
                />
                <button
                  type="button"
                  aria-label="Show password"
                  className="absolute right-2 opacity-40"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeCloseIcon /> : <EyeIcon />}
                </button>
              </div>
              {method.formState.errors.password && (
                <span className="text-sm text-destructive">
                  {method.formState.errors.password.message}
                </span>
              )}
            </div>
          )}
        />
        <button
          type="submit"
          className={clsx(
            "btn-purple btn-large flex-1 w-full !text-white transition-all hover:opacity-70",
            isDisabled && "opacity-50 cursor-not-allowed",
          )}
          // Disable button when form is invalid
          disabled={isDisabled}
        >
          Log in
        </button>
      </form>
    </FormProvider>
  );
}
